import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms'; // Import FormGroup and FormBuilder
import {
 EMAIL_VALIDATION_PATTERN,
  IMG_EXTENSIONS,
  Invalid_IMAGE_FORMAT,
  MULTILINGUAL_NAME_VALIDATION_PATTERN
} from '@shared/constant/shared.constant';
import { FileResponse } from '@core/models/file-response.model';
import { CustomValidator } from '@shared/services/custom-validatior.abstract';
import { AuthService } from '@core/services/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { SearchCountryField, CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input-gg';
import { ParkingLot } from '@core/models/parking-manager.model';
import { ParkingManagerService } from '@core/services/parking-manager.service';
import { ToastService } from '@shared/services/toast.service';
import { MenuService } from '@core/services/menu.service';
import { DialogControlService } from '@shared/services/dialog-control.service';
import { CommonFunctions } from '@shared/common-functions';
import { ProfileService } from '@core/services/profile.service';
import { autoUnsubWith } from '@shared/services/utility.abstract';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';
import { ConfigService } from '@core/services/config.service';

@Component({
  selector: 'app-manager-profile-component',
  templateUrl: './manager-profile-component.component.html',
  styleUrls: ['./manager-profile-component.component.scss']
})

export class ManagerProfileComponentComponent implements OnInit{
  readonly destroyRef = inject(DestroyRef);
  parkingLotId = []
  selectedCountries1: any
  @ViewChild('fileInput') fileInput!: ElementRef;
  formData: FormData = new FormData();
  imageUrl: string = ''
  id: string = ''
  status: boolean = false;
  managerForm: FormGroup;
  isProfileImgError = false;
  countryISO: CountryISO = CountryISO.UnitedStates;
  phoneNumberFormat = PhoneNumberFormat;
  separateDialCode = false;
  searchCountryField = SearchCountryField;
  preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedKingdom];
  previousUrl: string;
  dummy: string = "assets/images/dummy-img.png";
  parkingLotByStates: ParkingLot[] = [];
  requiredLastNameAsterisk: boolean = false;
  constructor(private readonly service: ProfileService,
    private readonly formBuilder: FormBuilder,
    private readonly toastService: ToastService,
    private readonly authService: AuthService,  
    private readonly menuService: MenuService,
    private readonly parkingMangerService: ParkingManagerService,
    public readonly dialogControlService: DialogControlService,
    private readonly encryptStorage: EncryptionStorageService,
    private readonly configService: ConfigService
    ) {
    const { validators, required } = CommonFunctions.getLastNameValidators('parkingManager', this.configService);
    this.requiredLastNameAsterisk = required;
    this.managerForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.pattern(MULTILINGUAL_NAME_VALIDATION_PATTERN), Validators.minLength(3), Validators.maxLength(50), CommonFunctions.noLeadingSpacesValidator()]],
      lastName: ['', [...validators,Validators.pattern(MULTILINGUAL_NAME_VALIDATION_PATTERN), Validators.minLength(3), Validators.maxLength(50), CommonFunctions.noLeadingSpacesValidator()]],
      email: ['', [Validators.required, Validators.pattern(EMAIL_VALIDATION_PATTERN)]],
      phone: ['',[Validators.required]],
      profileImagePath: ['', [this.fileSizeValidator(5)]],
      password: ['', [Validators.required,Validators.minLength(8)]],
      isActive: [this.status],
      parkingLotId: [this.parkingLotId,[Validators.required]]
    })
  }

  ngOnInit() {
    this.getParkingLotByStatesName()
    this.id = this.authService.getUserId();
  }

  submit() {
    const req = {
        email: this.managerForm.value?.email,
        name: this.managerForm.value?.name,
        lastName: this.managerForm.value?.lastName,
        password: this.managerForm.value?.password,
        mobileNumber: this.managerForm.value.phone?.number || '_',
        profileImagePath: this.managerForm.value?.profileImagePath,
        parkingLotId: this.parkingLotId,
        isActive: this.status,
        countryCode: this.managerForm.value.phone?.dialCode || '',
        iso: this.managerForm.value.phone?.countryCode || ''
    }
    this.isProfileImgError = false;
    if (this.managerForm.valid) {
      this.parkingMangerService.createParkingManager(req)
      .pipe(autoUnsubWith(this.destroyRef))
      .subscribe({
        next: (_response: any) => {
          this.toastService.showToastMessage('success', 'Success', 'Manager User Created Successfully');
          setTimeout(() => {
            this.menuService.redirectionTo(this.menuService.shortNameExists(JSON.parse(this.encryptStorage.getItem('menuData')), 17),'manager/managerlist' );
          }, 1000);
        },
        error: (er: HttpErrorResponse) => {
          this.toastService.showToastMessage('error', 'Error', er.error.message);
        }
      });
    } else {
      CustomValidator.markFormGroupTouched(this.managerForm);
    }
  }

onImageSelected(event: any) {
    const files = event.target.files[0];
    const maxSize = 5 * 1024 * 1024;
    if (files.size > maxSize) {
      this.isProfileImgError = true;
      return
    }
    this.isProfileImgError = false;
    this.formData.append('Images', files);
    const fileExtension = files.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !IMG_EXTENSIONS.includes(fileExtension)) {
      this.toastService.showToastMessage('error', 'Error', Invalid_IMAGE_FORMAT);
      return;
    }
    
    this.service.imageUplaod(this.formData)
      .pipe( autoUnsubWith(this.destroyRef))
      .subscribe({
        next:
          (response: FileResponse) => {
            const data = response;
            for (const key in data) {
              if (data.hasOwnProperty(key)) {
                const url = data[key];
                this.imageUrl = url
                this.managerForm.patchValue({
                  profileImagePath: url
                });
              }
            }
          }
      });
  }
  fileSizeValidator(maxSize: number) {
    return (control: AbstractControl) => {
      if (control.value) {
        const file = control.value as File; // Cast control.value to File
        if (file.size > maxSize * 1024 * 1024) { // Convert maxSize to bytes
          return {
            fileSizeExceedsLimit: true
          };
        }
      }
      return null;
    };
  }
  getParkingLotByStatesName() {
    this.parkingMangerService.getParkingLotByStatesName("")
      .pipe(autoUnsubWith(this.destroyRef)).subscribe({
        next: (response: ParkingLot[]) => {
          this.parkingLotByStates = response;
        }
      })
  }
  assignedParkingLots(id) {
    this.parkingLotId = id.value;
  }
  managerStatus() {
    this.status = !this.status
    this.managerForm.get('isActive')?.setValue(this.status);
  }
clearImage(){
  this.imageUrl=''
}
onKeyPress(event: KeyboardEvent): void {
  const allowedChars = /[0-9]/;
  if (!allowedChars.test(event.key)) {
    event.preventDefault();
  }
}
  onlyNumberInput(event: KeyboardEvent): void {
    CommonFunctions.onlyNumberInput(event);
  }
}
